import { useEffect, useState, type DragEvent } from "react";
import { api, type User } from "../lib/api";
import { normalizeCameraLayout, videoLayoutCapacity, videoLayouts, type VideoLayout } from "../lib/videoLayout";
import { WhepPlayer } from "../components/WhepPlayer";
import type { CameraSummary } from "./CamerasPage";

interface Props {
  user: User;
  onUserChange: (user: User) => void;
}

const pageOptions: ReadonlyArray<{ value: string; label: string; permission?: string }> = [
  { value: "dashboard", label: "운영 현황" },
  { value: "live", label: "실시간 보기", permission: "live.view" },
  { value: "playback", label: "녹화 영상", permission: "recording.search" },
  { value: "cameras", label: "카메라", permission: "camera.view" },
  { value: "storage", label: "저장소", permission: "storage.configure" },
  { value: "users", label: "사용자" },
  { value: "permissions", label: "권한", permission: "permission.manage" },
  { value: "system", label: "시스템", permission: "system.configure" },
  { value: "logs", label: "로그", permission: "log.view" },
  { value: "settings", label: "내 설정" }
];

const layoutLabels: Record<VideoLayout, string> = {
  "grid-1": "1분할",
  "grid-2": "2분할",
  "grid-4": "4분할",
  "grid-5": "5분할",
  "grid-6": "6분할",
  "grid-9": "9분할",
  "grid-10": "10분할",
  "grid-16": "16분할",
  "grid-20": "20분할"
};

interface PreviewStream { token: string; whepUrl: string }

export function PreferencesPage({ user, onUserChange }: Props) {
  const [defaultPage, setDefaultPage] = useState(user.defaultPage || "dashboard");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(user.sidebarCollapsed);
  const [videoLayout, setVideoLayout] = useState<VideoLayout>(user.videoLayout || "grid-4");
  const [cameraLayout, setCameraLayout] = useState<Array<string | null>>(user.cameraLayout ?? []);
  const [cameras, setCameras] = useState<CameraSummary[]>([]);
  const [previewStreams, setPreviewStreams] = useState<Record<string, PreviewStream>>({});
  const [draggedSlot, setDraggedSlot] = useState<number | null>(null);
  const [dragOverSlot, setDragOverSlot] = useState<number | null>(null);
  const [message, setMessage] = useState("");
  const availablePages = pageOptions.filter((page) => !page.permission || user.permissions.includes(page.permission));

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const items = await api<CameraSummary[]>("/api/camera-options?scope=any");
        if (cancelled) return;
        setCameras(items);
        setCameraLayout((current) => normalizeCameraLayout(items.map((camera) => camera.id), current, videoLayout));
        if (!user.permissions.includes("live.view")) return;
        const liveCameras = await api<CameraSummary[]>("/api/camera-options?scope=live");
        const streams = await Promise.all(liveCameras.filter((camera) => camera.enabled).map(async (camera) => {
          try {
            const stream = await api<PreviewStream>(`/api/cameras/${camera.id}/live-token`, { method: "POST" });
            return [camera.id, stream] as const;
          } catch {
            return null;
          }
        }));
        if (!cancelled) setPreviewStreams(Object.fromEntries(streams.filter((entry): entry is readonly [string, PreviewStream] => entry !== null)));
      } catch (error) {
        if (!cancelled) setMessage(error instanceof Error ? error.message : "카메라 목록 조회 실패");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const changeLayout = (next: VideoLayout) => {
    setVideoLayout(next);
    setCameraLayout((current) => normalizeCameraLayout(cameras.map((camera) => camera.id), current, next));
  };

  const changeCamera = (slot: number, cameraId: string) => {
    setCameraLayout((current) => {
      const next = [...current];
      const previous = next[slot] ?? null;
      const existingSlot = cameraId ? next.indexOf(cameraId) : -1;
      next[slot] = cameraId || null;
      if (existingSlot >= 0 && existingSlot !== slot) next[existingSlot] = previous;
      return next;
    });
  };

  const moveCamera = (from: number, to: number) => {
    if (from === to || from < 0 || to < 0 || from >= videoLayoutCapacity[videoLayout] || to >= videoLayoutCapacity[videoLayout]) return;
    setCameraLayout((current) => {
      const next = Array.from({ length: videoLayoutCapacity[videoLayout] }, (_, index) => current[index] ?? null);
      [next[from], next[to]] = [next[to], next[from]];
      return next;
    });
  };

  const startSlotDrag = (event: DragEvent<HTMLDivElement>, slot: number, hasCamera: boolean) => {
    if (!hasCamera) {
      event.preventDefault();
      return;
    }
    setDraggedSlot(slot);
    setDragOverSlot(slot);
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", String(slot));
  };

  const dropSlot = (event: DragEvent<HTMLDivElement>, slot: number) => {
    event.preventDefault();
    const transferred = Number(event.dataTransfer.getData("text/plain"));
    const from = draggedSlot ?? (Number.isInteger(transferred) ? transferred : -1);
    moveCamera(from, slot);
    setDraggedSlot(null);
    setDragOverSlot(null);
  };

  const save = async () => {
    const normalized = normalizeCameraLayout(cameras.map((camera) => camera.id), cameraLayout, videoLayout);
    try {
      await api("/api/preferences", { method: "PUT", body: JSON.stringify({ defaultPage, sidebarCollapsed, videoLayout, cameraLayout: normalized }) });
      setCameraLayout(normalized);
      onUserChange({ ...user, defaultPage, sidebarCollapsed, videoLayout, cameraLayout: normalized });
      setMessage("개인 설정을 저장했습니다. 실시간 보기와 녹화 영상에 같은 배치가 적용됩니다.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "개인 설정 저장 실패");
    }
  };

  return <>
    <div className="page-heading"><div><p className="eyebrow">개인 설정</p><h2>내 설정</h2></div></div>
    {message && <div className="alert alert-secondary py-2">{message}</div>}
    <div className="panel preferences-panel mb-4">
      <div className="panel-title"><h3>로그인 후 기본 화면</h3></div>
      <p className="text-secondary small">로그인하거나 NVR 주소로 접속했을 때 가장 먼저 표시할 화면을 선택합니다.</p>
      <label htmlFor="default-page">기본 화면</label>
      <select id="default-page" className="form-select" value={defaultPage} onChange={(event) => setDefaultPage(event.target.value)}>
        {availablePages.map((page) => <option key={page.value} value={page.value}>{page.label}</option>)}
      </select>
      <div className="form-check mt-4">
        <input id="sidebar-collapsed" type="checkbox" className="form-check-input" checked={sidebarCollapsed} onChange={(event) => setSidebarCollapsed(event.target.checked)} />
        <label className="form-check-label mt-0" htmlFor="sidebar-collapsed">사이드바 항상 접기</label>
      </div>
      <p className="text-secondary small mb-0">로그인할 때 왼쪽 메뉴를 아이콘만 보이도록 접습니다.</p>
    </div>

    <div className="panel preferences-layout-panel">
      <div className="panel-title"><h3>카메라 화면 배치</h3><span>실시간 보기 · 녹화 영상 공통</span></div>
      <p className="text-secondary small">분할 수와 각 위치에 표시할 카메라를 지정합니다. 미리보기 화면을 끌어 다른 분면에 놓거나 선택 목록을 사용하면 두 위치가 맞바뀌며, 비워 둔 위치는 검은 화면으로 유지됩니다.</p>
      <label>화면 분할 수</label>
      <div className="btn-group layout-choice-group" role="group" aria-label="화면 분할 수">
        {videoLayouts.map((layout) => <button key={layout} type="button" className={`btn btn-sm ${videoLayout === layout ? "btn-primary" : "btn-outline-secondary"}`} onClick={() => changeLayout(layout)}>{layoutLabels[layout]}</button>)}
      </div>

      <div className={`layout-preview-grid ${videoLayout} mt-3`} aria-label={`${layoutLabels[videoLayout]} 카메라 배치 미리보기`}>
        {Array.from({ length: videoLayoutCapacity[videoLayout] }, (_, index) => {
          const camera = cameras.find((item) => item.id === cameraLayout[index]);
          const stream = camera ? previewStreams[camera.id] : undefined;
          return <div
            key={index}
            className={`layout-preview-cell ${camera ? "" : "is-empty"} ${draggedSlot === index ? "is-dragging" : ""} ${dragOverSlot === index && draggedSlot !== index ? "is-drag-over" : ""}`}
            draggable={Boolean(camera)}
            data-layout-slot={index}
            aria-label={`${index + 1}번 위치${camera ? `, ${camera.name}` : ", 빈 화면"}`}
            onDragStart={(event) => startSlotDrag(event, index, Boolean(camera))}
            onDragEnter={(event) => {
              if (draggedSlot === null) return;
              event.preventDefault();
              setDragOverSlot(index);
            }}
            onDragOver={(event) => {
              if (draggedSlot === null) return;
              event.preventDefault();
              event.dataTransfer.dropEffect = "move";
            }}
            onDrop={(event) => dropSlot(event, index)}
            onDragEnd={() => {
              setDraggedSlot(null);
              setDragOverSlot(null);
            }}
          >
            {camera && stream
              ? <WhepPlayer key={camera.id} cameraId={camera.id} cameraName={camera.name} whepUrl={stream.whepUrl} token={stream.token} canSnapshot={false} canAudio={false} audioActive={false} onAudioToggle={() => undefined} minimal />
              : <div className="layout-preview-slot" aria-hidden="true" />}
          </div>;
        })}
      </div>

      <div className="layout-slot-editor mt-3">
        {Array.from({ length: videoLayoutCapacity[videoLayout] }, (_, index) => <div key={index}>
          <label htmlFor={`layout-slot-${index}`}>{index + 1}번 위치</label>
          <select id={`layout-slot-${index}`} className="form-select" value={cameraLayout[index] ?? ""} onChange={(event) => changeCamera(index, event.target.value)}>
            <option value="">빈 화면</option>
            {cameras.map((camera) => <option key={camera.id} value={camera.id}>{String(camera.position).padStart(2, "0")} · {camera.name}</option>)}
          </select>
        </div>)}
      </div>
      {!cameras.length && <p className="text-secondary small mt-3 mb-0">배치할 수 있는 카메라가 없습니다.</p>}
      <button type="button" className="btn btn-primary mt-4" onClick={() => void save()}>개인 설정 저장</button>
    </div>
  </>;
}
